import CloseIcon from '@mui/icons-material/Close'
import FmdGoodIcon from '@mui/icons-material/FmdGood'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import { useTranslation } from 'next-i18next'
import React from 'react'
import Wrapper from './Wrapper'
import { IProps, ISearchProps } from './types'

const buttonProps = {
  id: 'input-search',
  'aria-label': 'input-search'
}

const Search: React.FC<IProps> = ({
  params,
  searchValue,
  placeholder,
  handleOnChange,
  handleKeyDown,
  handleOnClick,
  clearSearch,
  buttonLabel
}) => {
  const { t } = useTranslation('common')
  return (
    <div className="relative mx-auto w-full max-w-[600px]">
      <div
        className={`
          h-14 w-full rounded-2xl border border-grey-400
          bg-white-500 shadow-lg transition-all
          duration-200 ease-in-out
          focus-within:shadow-xl hover:shadow-xl
        `}
      >
        <div
          className="flex h-full items-center"
          ref={params?.slotProps?.input?.ref}
        >
          {/* Location Icon */}
          <div className="mr-4 ml-6 flex items-center">
            <FmdGoodIcon className="size-6 fill-blue-500 transition-colors duration-200" />
          </div>

          {/* Input */}
          <input
            {...(params?.slotProps?.htmlInput || {})}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            value={searchValue}
            placeholder={placeholder}
            className={`
              size-full bg-transparent text-base font-medium text-black-500
              placeholder:text-center placeholder:text-base placeholder:text-black-300
              focus:outline-none
            `}
          />

          <div className="flex h-full items-center px-2">
            {!!searchValue && (
              <IconButton
                id="close-search"
                aria-label={t('A11Y.CLEAR_SEARCH')}
                onClick={clearSearch}
                size="small"
                className="hover:fill-blue-500"
              >
                <CloseIcon className="size-5 fill-black-300 transition-colors duration-200 hover:fill-blue-500" />
              </IconButton>
            )}

            {/* Desktop Search Button */}
            <button
              {...buttonProps}
              id="search-button-desktop"
              onClick={handleOnClick}
              className={`
                mx-2 hidden h-10 min-h-10 w-28 min-w-28 items-center
                justify-center rounded-xl
                bg-blue-500 p-0 text-sm
                font-medium transition-all duration-200
                ease-in-out hover:-translate-y-0.5 hover:bg-blue-600
                hover:shadow-lg md:flex
              `}
            >
              <span className="text-white-500">
                {buttonLabel ?? t('SEARCH_BAR.SEARCH')}
              </span>
            </button>

            {/* Mobile Search Button */}
            <button
              {...buttonProps}
              id="search-button-mobile"
              onClick={handleOnClick}
              className={`
                mx-2 flex size-10 min-h-10 min-w-10 items-center
                justify-center rounded-xl
                bg-blue-500 p-0 transition-all
                duration-200 ease-in-out hover:-translate-y-0.5
                hover:bg-blue-600 hover:shadow-lg md:hidden
              `}
            >
              <SearchIcon className="size-5 fill-white-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const MemoSearchButton: typeof Search = React.memo(Search)
const Wrapped: React.FC<ISearchProps> = (props) => (
  <Wrapper {...props} Input={MemoSearchButton} />
)

export default React.memo(Wrapped)
